import { useEffect, useState } from "react";

import { IContextualMenuItem } from "@bps/fluent-ui";
import { OutboundCommChannel } from "@libs/gateways/comms/CommsGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { FormTemplateDTO } from "@libs/gateways/forms/FormsGateway.dtos.ts";
import { CommunicationType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { ContextMenuItemsEnum } from "@modules/booking/screens/booking-calendar/components/booking-calendar-event/contextual-menu/ContextMenuItemsEnum.ts";
import { ContactPreferences } from "@stores/comms/models/ContactPreferences.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { findCommValue } from "@stores/practice/utils/practice.utils.ts";

import {
  GetSubMenuItemsOptions,
  UseFormMenuOptions
} from "./form-context-menu.types.ts";
import { getSubMenuItems } from "./form-context-menu.utils.tsx";

export const useFormMenu = (
  contactOrId?: Contact | string,
  options?: UseFormMenuOptions
): IContextualMenuItem[] => {
  const { forms, comms } = useStores();

  const [clinicalTemplates, setClinicalTemplates] = useState<
    FormTemplateDTO[] | undefined
  >();

  const [nonClinicalTemplates, setNonClinicalTemplates] = useState<
    FormTemplateDTO[] | undefined
  >();

  const [contactPreferences, setContactPreferences] = useState<
    ContactPreferences | undefined
  >();

  const contact =
    typeof contactOrId === "string"
      ? forms.ui.practice.contactsMap.get(contactOrId)
      : contactOrId;

  useEffect(() => {
    if (!contact?.id) return;
    comms
      .getContactPreference(contact.id)
      .then(result => setContactPreferences(result));
  }, [contact?.id, comms]);

  if (
    !contact?.isPatient ||
    !forms.ui.core.hasPermissions(Permission.SendForms)
  )
    return [];

  const subMenuOptions: GetSubMenuItemsOptions = {
    patientId: contact.id,
    claimId: options?.claimId,
    addContextBeforeDeployAction: options?.addContextBeforeDeployAction,
    formUi: forms.ui,
    clinicalTemplates: options?.filterForms
      ? clinicalTemplates?.filter(options.filterForms)
      : clinicalTemplates,
    nonClinicalTemplates: options?.filterForms
      ? nonClinicalTemplates?.filter(options.filterForms)
      : nonClinicalTemplates,
    setTemplates: (data: {
      clinicalTemplates: FormTemplateDTO[];
      nonClinicalTemplates: FormTemplateDTO[];
    }) => {
      setClinicalTemplates(data.clinicalTemplates);
      setNonClinicalTemplates(data.nonClinicalTemplates);
    }
  };

  // These styles are there to fix a bug where the forms picker would
  // have an ellipsis and not show the full name of some of the forms, but
  // only when there are enough forms to have a scrollbar. This issue may be
  // fixed on the component level in future, in which case this can be removed.
  const menuStyles = {
    root: { width: "fit-content" },
    subComponentStyles: {
      menuItem: { root: { paddingRight: 16 } }
    }
  };

  const formNotifyPref = contactPreferences?.formNotifyPreferences;

  const optedOut = formNotifyPref?.contactHasOptedOut ?? false;
  const preferredChannel = formNotifyPref?.preferredCommChannelTypeCode;

  const mobile = findCommValue(
    contact.communications,
    CommunicationType.Mobile
  );

  const email = findCommValue(contact.communications, CommunicationType.Email);

  const canSms = !!mobile && !optedOut;
  const canEmail = !!email && !optedOut;

  const isSmsPreferred = preferredChannel === OutboundCommChannel.Sms;
  const isEmailPreferred = preferredChannel === OutboundCommChannel.Email;

  const sendSmsForm: IContextualMenuItem | null = canSms
    ? {
        key: ContextMenuItemsEnum.SmsForm,
        text: isSmsPreferred ? "SMS form (preferred)" : "SMS form",
        itemProps: isSmsPreferred
          ? { styles: { root: { fontWeight: 600 } } }
          : undefined,
        subMenuProps: {
          items: getSubMenuItems(subMenuOptions),
          styles: menuStyles
        }
      }
    : null;

  const sendForm: IContextualMenuItem | null = canEmail
    ? {
        key: ContextMenuItemsEnum.SendFormKey,
        text: isEmailPreferred ? "Email form (preferred)" : "Email form",
        itemProps: isEmailPreferred
          ? { styles: { root: { fontWeight: 600 } } }
          : undefined,
        subMenuProps: {
          items: getSubMenuItems(subMenuOptions),
          styles: menuStyles
        }
      }
    : null;

  const showQR: IContextualMenuItem = {
    key: ContextMenuItemsEnum.QRCodeKey,
    text: ContextMenuItemsEnum.QRCode,
    qrCode: true,
    subMenuProps: {
      items: getSubMenuItems({
        ...subMenuOptions,
        qrcode: true
      }),
      styles: menuStyles
    }
  };

  const items = [sendSmsForm, sendForm, showQR].filter(
    (item): item is IContextualMenuItem => item !== null
  );

  if (options?.asSubmenu) {
    return [
      {
        key: "forms",
        text: ContextMenuItemsEnum.Forms,
        subMenuProps: { items }
      }
    ];
  }

  return items;
};
